import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import API_URL from '../lib/apiUrl';

const SubscriptionContext = createContext(null);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { token, isAuthenticated } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSubscription = useCallback(async () => {
    if (!token || !isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/subscription/my-plan`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscription(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
      setError(err.response?.data?.detail || 'Failed to load subscription');
    } finally {
      setLoading(false);
    }
  }, [token, isAuthenticated]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const checkLimit = async (limitType) => {
    if (!token) return { allowed: false, message: 'Not authenticated' };

    try {
      const response = await axios.get(
        `${API_URL}/api/subscription/check-limit/${limitType}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (err) {
      console.error('Failed to check limit:', err);
      return {
        allowed: false,
        message: err.response?.data?.detail || 'Failed to check subscription limit'
      };
    }
  };

  const canCreateAssessment = () => {
    if (!subscription) return true; // Allow if subscription not loaded yet
    return subscription.can_create_assessment;
  };

  const canAddUser = () => {
    if (!subscription) return true;
    return subscription.can_add_user;
  };

  const hasFeature = (feature) => {
    if (!subscription) return true; // Allow if subscription not loaded yet
    return subscription.features?.includes(feature);
  };

  const getPlanName = () => {
    return subscription?.plan_name || 'Unknown';
  };

  const getUsagePercentage = (type) => {
    if (!subscription) return 0;
    const usage = subscription.usage?.[type] || 0;
    const limit = subscription.limits?.[type] || 1;
    return Math.round((usage / limit) * 100);
  };

  const value = {
    subscription,
    loading,
    error,
    fetchSubscription,
    checkLimit,
    canCreateAssessment,
    canAddUser,
    hasFeature,
    getPlanName,
    getUsagePercentage,
    isStarter: subscription?.plan === 'starter',
    isStandard: subscription?.plan === 'standard',
    isPro: subscription?.plan === 'pro',
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
